import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Shield } from 'lucide-react';
import { comparisons } from '../data/comparisons';

const SITE = 'https://githubmate.ai';

const CompareIndex: React.FC = () => {
  const url = `${SITE}/compare`;
  const title = 'GitHubMate Comparisons — Free AI Code Security Scanner vs Alternatives (2026)';
  const description =
    'Compare GitHubMate with Snyk, SonarQube, Semgrep and CodeRabbit. See how a free, no-signup OWASP LLM Top 10 scanner stacks up against the leading AI code analysis tools.';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${url}#webpage`,
    url,
    name: title,
    description,
    isPartOf: { '@id': `${SITE}/#website` },
    hasPart: comparisons.map((c) => ({
      '@type': 'WebPage',
      name: `GitHubMate vs ${c.competitor}`,
      url: `${SITE}/compare/githubmate-vs-${c.slug}`,
    })),
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-charcoal-bg min-h-full">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <header className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              GitHubMate vs the alternatives
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              How a free, no-signup OWASP LLM Top 10 scanner compares to the leading AI code
              analysis tools.
            </p>
          </header>

          <div className="grid sm:grid-cols-2 gap-5 mb-12">
            {comparisons.map((c) => (
              <Link
                key={c.slug}
                to={`/compare/githubmate-vs-${c.slug}`}
                className="card hover:shadow-lg transition-shadow group"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center justify-between">
                  GitHubMate vs {c.competitor}
                  <ArrowRight className="h-5 w-5 text-blue-600 group-hover:translate-x-1 transition-transform" />
                </h2>
                <p className="text-gray-600 text-sm">{c.tagline}</p>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link to="/" className="btn-primary inline-flex items-center">
              <Shield className="h-4 w-4 mr-2" /> Scan a repository free
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompareIndex;
